import {
  AggregateIllegalRecordsRequest,
  ApiError,
  GetIllegalRecordRequest,
  IllegalRecordHandleResult,
  IllegalRecordHandleState,
  ListIllegalRecordsRequest,
  VehicleProp,
} from '@36node-fcp/core-sdk';
import { Badge, Select, SelectProps } from 'antd';
import { isEmpty } from 'lodash';
import React from 'react';

import { useQuery } from 'src/lib/react-api';
import { fcp } from 'src/services';

/**
 * Api Error
 */
export const IllegalRecordApiErrorMap: { [key: string]: { [key: string]: string } } = {
  [ApiError.Code.DUPLICATE]: {},
};

/**
 * 违法记录状态
 */
type IllegalRecordStateUi = {
  text: string;
  color: string;
  value: string;
};

export const IllegalRecordExpireUiList: IllegalRecordStateUi[] = [
  { text: '未过期', color: '#FAAD14', value: 'EFFECTIVE' },
  { text: '过期', color: '#8c8c8c', value: 'EXPIRED' },
];

export const IllegalRecordExpireFilter = IllegalRecordExpireUiList;
export const toIllegalRecordExpireText = (value: string) =>
  IllegalRecordExpireUiList.find((item) => item.value === value)?.text;

export const IllegalRecordExpireBadge: React.FC<{ state: string }> = ({ state }) => {
  const ui = IllegalRecordExpireUiList.find((item) => item.value === state);
  if (!ui) return null;
  return <Badge color={ui.color} text={ui.text} />;
};

export const IllegalRecordStateUiList: IllegalRecordStateUi[] = [
  { text: '未处理', color: '#FAAD14', value: 'OPEN' },
  { text: '已发送', color: '#5197FA', value: 'PUSHED' },
  { text: '已关闭', color: '#25D2A0', value: 'CLOSED' },
];

export const IllegalRecordStateFilter = IllegalRecordStateUiList;
export const toIllegalRecordStateText = (value: string) =>
  IllegalRecordStateUiList.find((item) => item.value === value)?.text;
export const toIllegalRecordStateValue = (text: string) =>
  IllegalRecordStateUiList.find((item) => item.text === text)?.value;

export const IllegalRecordStateBadge: React.FC<{ state: string }> = ({ state }) => {
  const ui = IllegalRecordStateUiList.find((item) => item.value === state);
  if (!ui) return null;
  return <Badge color={ui.color} text={ui.text} />;
};

export function IllegalRecordStateSelect(props: SelectProps) {
  return (
    <Select {...props}>
      {IllegalRecordStateUiList.map((item) => (
        <Select.Option key={item.value} value={item.value}>
          <Badge color={item.color} text={item.text} />
        </Select.Option>
      ))}
    </Select>
  );
}

/**
 * 获取违法记录列表
 * @param req 违法记录列表请求参数
 * @returns [state, listIllegalRecords]
 */
export const useIllegalRecordList = (req: ListIllegalRecordsRequest) => {
  const [state, listIllegalRecords] = useQuery(fcp.listIllegalRecords, req || {});
  return [state, listIllegalRecords] as const;
};

/**
 * 获取违法记录统计
 */
export const useIllegalRecordAggregation = (req: AggregateIllegalRecordsRequest) => {
  const [state, aggregateIllegalRecords] = useQuery(fcp.aggregateIllegalRecords, req || {});
  return [state, aggregateIllegalRecords] as const;
};

/**
 * 车辆使用性质
 */
export const VehiclePropertyUiList = [
  { text: '大客车', value: VehicleProp.BUS },
  { text: '中客车', value: VehicleProp.MIDDLEBUS },
  { text: '集装箱车', value: VehicleProp.CONTAINERCAR },
  { text: '大件运输车', value: VehicleProp.LARGETRANSPORT },
  { text: '危化品车', value: VehicleProp.DANGERCHAMICAL },
  { text: '货车', value: VehicleProp.TRUCK },
  { text: 'J2', value: VehicleProp.J2 },
  { text: '疑似货车', value: VehicleProp.SUSPECTEDTRUCK },
];
export const VehiclePropertyFilter = VehiclePropertyUiList;
export const toVehiclePropertyText = (value: string) =>
  VehiclePropertyUiList.find((item) => item.value === value)?.text || '--';
export const toVehiclePropertyValue = (text: string) => VehiclePropertyUiList.find((item) => item.text === text)?.value;

export function VehiclePropertySelect(props: SelectProps) {
  return (
    <Select {...props}>
      {VehiclePropertyUiList.map((item) => (
        <Select.Option key={item.value} value={item.value}>
          {item.text}
        </Select.Option>
      ))}
    </Select>
  );
}

export const useIllegalRecord = (req?: GetIllegalRecordRequest) => {
  const [state, getIllegalRecord] = useQuery(fcp.getIllegalRecord, req);
  return [state, getIllegalRecord] as const;
};

export const IllegalRecordHandleStateUiList: IllegalRecordStateUi[] = [
  { text: '未处置', color: '#FAAD14', value: IllegalRecordHandleState.OPEN },
  { text: '处置完成', color: '#25D2A0', value: IllegalRecordHandleState.CLOSED },
];

export const IllegalRecordHandleStateFilter = IllegalRecordHandleStateUiList;
export const toIllegalRecordHandleStateText = (value: string) =>
  IllegalRecordHandleStateUiList.find((item) => item.value === value)?.text;
export const toIllegalRecordHandleStateValue = (text: string) =>
  IllegalRecordHandleStateUiList.find((item) => item.text === text)?.value;
export const IllegalRecordHandleStateBadge: React.FC<{ state: string }> = ({ state }) => {
  const ui = IllegalRecordHandleStateUiList.find((item) => item.value === state);
  if (!ui) return null;
  return <Badge color={ui.color} text={ui.text} />;
};

/**
 * 违法处置结果
 */
export const IllegalHandleResultUiList = [
  { text: '电话通知非驾驶员完成', value: IllegalRecordHandleResult.NOTIFY_NON_DRIVER_COMPLETED },
  { text: '电话通知驾驶员完成', value: IllegalRecordHandleResult.NOTIFY_DRIVER_COMPLETED },
  { text: '电话未接通', value: IllegalRecordHandleResult.PHONE_NOT_CONNECTED },
  { text: '电话警告教育', value: IllegalRecordHandleResult.PHONE_WARNING_EDUCATION },
  { text: '情报板提醒', value: IllegalRecordHandleResult.INTELLIGENCE_BOARD_REMINDER },
  { text: '劝离高速', value: IllegalRecordHandleResult.PERSUADE_TO_LEAVE_HIGHWAY },
  { text: '联勤干预', value: IllegalRecordHandleResult.JOINT_INTERVENTION },
];
export const toIllegalRecordHandleResultText = (value: IllegalRecordHandleResult) =>
  IllegalHandleResultUiList.find((item) => item.value === value)?.text;
export const toIllegalRecordHandleResultTexts = (value: IllegalRecordHandleResult[], desc?: string) => {
  let ret = '';
  if (!isEmpty(value)) {
    ret += value.map((val) => IllegalHandleResultUiList.find((item) => item.value === val)?.text).join(',');
  }
  if (desc) {
    ret += ',' + desc;
  }

  if (ret) {
    return ret;
  }
  return '--';
};
